import React, { useEffect, useState } from 'react';
import { PageHeader } from '../components/common/PageHeader';
import { useFeedback } from '../components/common/Feedback';
import { CreditCard, AlertCircle, CheckCircle2, XCircle, Loader2, ExternalLink, Clock } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useOrg } from '../hooks/useOrg';
import { billingApi, BillingStatus } from '../api/billing';
import { formatDate } from '../utils/formatters';
import { AppLayout } from '../components/Layout/AppLayout';

const Billing: React.FC = () => {
  const { confirmar } = useFeedback();
  const { membership } = useAuth();
  const { org } = useOrg();
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    // Check URL params for post-checkout feedback
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setSuccessMsg('Assinatura realizada com sucesso! Seu acesso foi ativado.');
      // Clean the URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('canceled') === 'true') {
      setError('O checkout foi cancelado. Você pode tentar novamente quando quiser.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    fetchBillingStatus();
  }, []);

  const fetchBillingStatus = async () => {
    try {
      setLoading(true);
      const data = await billingApi.getStatus();
      setStatus(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao carregar informações de faturamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      setActionLoading(true);
      const { redirect_url } = await billingApi.subscribe();
      window.location.href = redirect_url;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao iniciar assinatura');
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!(await confirmar({ titulo: 'Cancelar a assinatura?', mensagem: 'O acesso continua até o fim do período já pago.', confirmar: 'Cancelar assinatura', cancelar: 'Manter assinatura', perigo: true }))) {
      return;
    }

    try {
      setActionLoading(true);
      await billingApi.cancel();
      await fetchBillingStatus();
      setSuccessMsg('Assinatura será cancelada ao final do período atual.');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao cancelar assinatura');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePortal = async () => {
    try {
      setActionLoading(true);
      const { redirect_url } = await billingApi.portal();
      window.location.href = redirect_url;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao abrir portal de pagamentos');
      setActionLoading(false);
    }
  };

  const getStatusLabel = (statusValue: string) => {
    const labels: Record<string, { text: string; color: string; bg: string; icon: React.ReactNode }> = {
      active: { text: 'Ativa', color: 'var(--color-primary-400)', bg: 'rgba(43, 184, 126, 0.1)', icon: <CheckCircle2 style={{ width: '16px', height: '16px' }} /> },
      trialing: { text: 'Período de Teste', color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.1)', icon: <Clock style={{ width: '16px', height: '16px' }} /> },
      past_due: { text: 'Pagamento Pendente', color: '#facc15', bg: 'rgba(250, 204, 21, 0.1)', icon: <AlertCircle style={{ width: '16px', height: '16px' }} /> },
      canceled: { text: 'Cancelada', color: 'var(--color-error)', bg: 'rgba(239, 68, 68, 0.1)', icon: <XCircle style={{ width: '16px', height: '16px' }} /> },
      incomplete: { text: 'Incompleta', color: '#facc15', bg: 'rgba(250, 204, 21, 0.1)', icon: <AlertCircle style={{ width: '16px', height: '16px' }} /> },
      pending: { text: 'Pendente', color: '#facc15', bg: 'rgba(250, 204, 21, 0.1)', icon: <Clock style={{ width: '16px', height: '16px' }} /> },
    };
    return labels[statusValue] || labels.pending;
  };



  if (membership?.role !== 'owner') {
    return (
      <AppLayout>
        <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
          <AlertCircle style={{ width: '48px', height: '48px', margin: '0 auto var(--space-4)', opacity: 0.5 }} />
          <h2>Apenas o proprietário da organização pode acessar esta página.</h2>
        </div>
      </AppLayout>
    );
  }

  if (loading) {
    return (
      <AppLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Loader2 style={{ width: '32px', height: '32px', color: 'var(--color-primary-500)', animation: 'spin 1s linear infinite' }} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* M9: mesmo cabeçalho e mesmo alinhamento à esquerda das outras telas */}
      <PageHeader
        title="Plano e cobrança"
        icon={<CreditCard size={24} style={{ color: 'var(--color-primary-500)', flexShrink: 0 }} />}
        description={`Gerencie o plano da organização ${org?.name ?? ''}`}
      />
      <div style={{ maxWidth: '800px', display: 'flex', flexDirection: 'column', gap: 'var(--space-8)' }}>

        {successMsg && (
          <div style={{ padding: 'var(--space-4)', borderRadius: 'var(--radius-lg)', background: 'rgba(43, 184, 126, 0.1)', border: '1px solid rgba(43, 184, 126, 0.2)', color: 'var(--color-primary-400)', display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
            <CheckCircle2 style={{ width: '20px', height: '20px', flexShrink: 0, marginTop: '2px' }} />
            <p>{successMsg}</p>
          </div>
        )}

        {error && (
          <div style={{ padding: 'var(--space-4)', borderRadius: 'var(--radius-lg)', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)', color: 'var(--color-error)', display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
            <AlertCircle style={{ width: '20px', height: '20px', flexShrink: 0, marginTop: '2px' }} />
            <p>{error}</p>
          </div>
        )}

        {status?.billing_exempt && (
          <div style={{ padding: 'var(--space-6)', borderRadius: 'var(--radius-xl)', border: '1px solid rgba(26, 154, 102, 0.2)', background: 'rgba(26, 154, 102, 0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
              <CheckCircle2 style={{ width: '32px', height: '32px', color: 'var(--color-primary-400)', flexShrink: 0 }} />
              <div>
                <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-medium)', color: 'var(--color-primary-400)', marginBottom: 'var(--space-1)' }}>Acesso Gratuito Irrestrito</h2>
                <p style={{ color: 'var(--color-primary-400)', opacity: 0.8 }}>
                  Sua organização possui uma isenção especial. Você pode utilizar todas as funcionalidades
                  sem cobranças ou limite de usuários.
                </p>
              </div>
            </div>
          </div>
        )}

        {!status?.billing_exempt && status?.is_trial && !status?.subscription && (
          <div style={{ padding: 'var(--space-6)', borderRadius: 'var(--radius-xl)', border: '1px solid rgba(59, 130, 246, 0.2)', background: 'rgba(59, 130, 246, 0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
              <AlertCircle style={{ width: '32px', height: '32px', color: '#60a5fa', flexShrink: 0 }} />
              <div>
                <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-medium)', color: '#60a5fa', marginBottom: 'var(--space-1)' }}>Período de Teste Ativo</h2>
                <p style={{ color: '#60a5fa', opacity: 0.8 }}>
                  Sua organização está no período de teste gratuito de 7 dias, que encerra em{' '}
                  <strong style={{ color: 'var(--color-text-primary)' }}>{status.trial_end_date ? formatDate(status.trial_end_date) : ''}</strong>.
                  Assine agora para não perder o acesso.
                </p>
              </div>
            </div>
          </div>
        )}

        {!status?.billing_exempt && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-6)' }}>
            <div className="glass" style={{ padding: 'var(--space-6)', borderRadius: 'var(--radius-xl)' }}>
              <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-primary)', marginBottom: 'var(--space-4)' }}>Status do Plano</h2>

              {status?.subscription ? (() => {
                const statusInfo = getStatusLabel(status.subscription.status);
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3)', borderRadius: 'var(--radius-lg)', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <span style={{ color: 'var(--color-text-secondary)' }}>Status</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: statusInfo.color, background: statusInfo.bg, padding: '4px 10px', borderRadius: 'var(--radius-full)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}>
                        {statusInfo.icon} {statusInfo.text}
                      </span>
                    </div>

                    {status.subscription.cancel_at_period_end && (
                      <div style={{ padding: 'var(--space-3)', borderRadius: 'var(--radius-lg)', background: 'rgba(250, 204, 21, 0.1)', border: '1px solid rgba(250, 204, 21, 0.2)', color: '#facc15', fontSize: 'var(--text-sm)' }}>
                        ⚠️ A assinatura será cancelada ao final do período atual
                        {status.subscription.current_period_end && (
                          <> em <strong>{formatDate(status.subscription.current_period_end)}</strong></>
                        )}
                      </div>
                    )}



                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3)', borderRadius: 'var(--radius-lg)', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <span style={{ color: 'var(--color-text-secondary)' }}>Usuários Extras</span>
                      <span style={{ color: 'var(--color-text-primary)', fontWeight: 'var(--font-medium)' }}>{status.subscription.extra_users}</span>
                    </div>

                    {status.subscription.current_period_end && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-3)', borderRadius: 'var(--radius-lg)', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <span style={{ color: 'var(--color-text-secondary)' }}>Próxima Cobrança</span>
                        <span style={{ color: 'var(--color-text-primary)', fontWeight: 'var(--font-medium)' }}>{formatDate(status.subscription.current_period_end)}</span>
                      </div>
                    )}

                    {/* Stripe Customer Portal button */}
                    {status.subscription.stripe_customer_id && (
                      <button
                        onClick={handlePortal}
                        disabled={actionLoading}
                        style={{ width: '100%', marginTop: 'var(--space-2)', padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-lg)', background: 'rgba(255, 255, 255, 0.1)', color: 'var(--color-text-primary)', fontWeight: 'var(--font-medium)', border: '1px solid rgba(255, 255, 255, 0.15)', transition: 'background 0.2s', opacity: actionLoading ? 0.5 : 1, cursor: actionLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)' }}
                      >
                        <ExternalLink style={{ width: '16px', height: '16px' }} />
                        {actionLoading ? 'Processando...' : 'Gerenciar Pagamentos'}
                      </button>
                    )}

                    {status.subscription.status === 'active' && !status.subscription.cancel_at_period_end && (
                      <button
                        onClick={handleCancel}
                        disabled={actionLoading}
                        style={{ width: '100%', padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-lg)', background: 'rgba(239, 68, 68, 0.1)', color: 'var(--color-error)', border: '1px solid rgba(239, 68, 68, 0.2)', transition: 'background 0.2s', opacity: actionLoading ? 0.5 : 1, cursor: actionLoading ? 'not-allowed' : 'pointer' }}
                      >
                        {actionLoading ? 'Processando...' : 'Cancelar Assinatura'}
                      </button>
                    )}

                    {(status.subscription.status === 'canceled' || status.subscription.status === 'incomplete') && (
                      <button
                        onClick={handleSubscribe}
                        disabled={actionLoading}
                        style={{ width: '100%', padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-lg)', background: 'var(--color-primary-500)', color: 'white', fontWeight: 'var(--font-medium)', border: 'none', transition: 'background 0.2s', opacity: actionLoading ? 0.5 : 1, cursor: actionLoading ? 'not-allowed' : 'pointer' }}
                      >
                        {actionLoading ? 'Processando...' : 'Reativar Assinatura'}
                      </button>
                    )}
                  </div>
                );
              })() : (
                <div style={{ textAlign: 'center', padding: 'var(--space-6) 0' }}>
                  <AlertCircle style={{ width: '48px', height: '48px', color: 'var(--color-text-secondary)', margin: '0 auto var(--space-3)' }} />
                  <h3 style={{ color: 'var(--color-text-primary)', fontWeight: 'var(--font-medium)', marginBottom: 'var(--space-2)' }}>Sem assinatura ativa</h3>
                  <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-6)' }}>
                    Assine o plano recorrente para continuar usando a plataforma.
                  </p>
                  <button
                    onClick={handleSubscribe}
                    disabled={actionLoading}
                    style={{ width: '100%', padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-lg)', background: 'var(--color-primary-500)', color: 'white', fontWeight: 'var(--font-medium)', border: 'none', transition: 'background 0.2s', opacity: actionLoading ? 0.5 : 1, cursor: actionLoading ? 'not-allowed' : 'pointer' }}
                  >
                    {actionLoading ? 'Processando...' : 'Assinar Agora'}
                  </button>
                </div>
              )}
            </div>

            <div className="glass" style={{ padding: 'var(--space-6)', borderRadius: 'var(--radius-xl)' }}>
              <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-primary)', marginBottom: 'var(--space-4)' }}>Detalhes do Plano</h2>
              <ul style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--color-neutral-300)' }}>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
                  <CheckCircle2 style={{ width: '20px', height: '20px', color: 'var(--color-primary-500)', flexShrink: 0 }} />
                  <span>Gestão ilimitada de credenciais</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
                  <CheckCircle2 style={{ width: '20px', height: '20px', color: 'var(--color-primary-500)', flexShrink: 0 }} />
                  <span>Base de 10 usuários inclusos (R$ 299)</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
                  <CheckCircle2 style={{ width: '20px', height: '20px', color: 'var(--color-primary-500)', flexShrink: 0 }} />
                  <span>Usuários adicionais: R$ 10/mês cada</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
                  <CheckCircle2 style={{ width: '20px', height: '20px', color: 'var(--color-primary-500)', flexShrink: 0 }} />
                  <span>Extensão para navegadores (Autofill + TOTP)</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
                  <CheckCircle2 style={{ width: '20px', height: '20px', color: 'var(--color-primary-500)', flexShrink: 0 }} />
                  <span>Registro de atividades e criptografia AES-256</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
                  <CheckCircle2 style={{ width: '20px', height: '20px', color: 'var(--color-primary-500)', flexShrink: 0 }} />
                  <span>Pagamento seguro via Stripe</span>
                </li>
              </ul>
            </div>
          </div>
        )}

      </div>
    </AppLayout>
  );
};

export default Billing;
